import GateKeeperMiddleware from '@/middleware/GateKeeperMiddleware.js';

export default [
    {
        path: '/courses',
        component: () => import('@/pages/courses/Courses.vue'),
        children: [
            {
                name: 'courses',
                path: '',
                component: () => import('@/components/courses/CourseList.vue'),
                meta: {
                    title: 'Courses',
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view all', subject: 'courses' },
                },
            },
        ],
    },
    {
        path: '/courses/:courseId',
        component: () => import('@/pages/courses/Course.vue'),
        meta: {
            title: 'Course',
            middleware: [GateKeeperMiddleware],
            ability: ({ gateKeeper, to }) => gateKeeper.userHasAccessToCourse(to.params.courseId),
        },
        props: route => ({
            courseId: Number.parseInt(route.params.courseId, 10),
        }),
        children: [
            {
                name: 'course-dashboard',
                path: '',
                component: () => import('@/pages/courses/CourseDashboard.vue'),
                meta: {
                    title: 'Course',
                    middleware: [GateKeeperMiddleware],
                    ability: ({ gateKeeper, to }) => gateKeeper.userHasAccessToCourse(to.params.courseId),
                },
            },
            {
                name: 'course-edit',
                path: 'edit',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Edit Course',
                    middleware: [GateKeeperMiddleware],
                    ability: ({ gateKeeper, to }) => gateKeeper.userHasAccessToEditCourse(to.params.courseId),
                },
            },
            {
                name: 'course-edit-content',
                path: 'edit/content',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Edit Course Content',
                    middleware: [GateKeeperMiddleware],
                    ability: [{ action: 'edit', subject: 'course' }],
                },
            },
            {
                name: 'course-edit-settings',
                path: 'edit/settings',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Edit Course Settings',
                    middleware: [GateKeeperMiddleware],
                    ability: [{ action: 'edit', subject: 'course' }],
                },
            },
            {
                name: 'course-edit-grading-scheme',
                path: 'edit/grading-scheme',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Edit Course Grading Scheme',
                    middleware: [GateKeeperMiddleware],
                    ability: [{ action: 'edit', subject: 'course' }],
                },
            },
            {
                name: 'course-edit-schedule',
                path: 'edit/schedule',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Edit Course Schedule',
                    middleware: [GateKeeperMiddleware],
                    ability: [
                        { action: 'edit', subject: 'course' },
                        { action: 'edit', subject: 'time period' },
                    ],
                },
            },
            {
                name: 'course-edit-remediation',
                path: 'edit/remediation',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Remediation',
                    middleware: [GateKeeperMiddleware],
                    ability: [{ action: 'edit', subject: 'course' }],
                },
            },
            {
                name: 'course-remediation',
                path: 'remediation',
                component: () => import('@/pages/courses/remediation/CourseRemediation.vue'),
                meta: {
                    title: 'Remediation Page',
                    middleware: [GateKeeperMiddleware],
                    ability: ({ gateKeeper, to }) =>
                        gateKeeper.userHasPermission('view all remediation processes') ||
                        gateKeeper.userHasGroupPermissionWithinCourse(
                            to.params.courseId,
                            'view all remediation processes'
                        ) ||
                        gateKeeper.user.isInstructorOf(to.params.courseId),
                },
            },
            {
                name: 'course-edit-automated-surveys',
                path: 'edit/evaluations',
                component: () => import('@/pages/courses/CourseEdit.vue'),
                meta: {
                    title: 'Evaluations',
                    middleware: [GateKeeperMiddleware],
                    ability: [{ action: 'edit', subject: 'course' }],
                },
            },

            {
                name: 'course-reports',
                path: 'course-reports',
                component: () => import('@/pages/courses/course-reports/CourseReports.vue'),
                meta: {
                    title: 'Course Reports',
                    middleware: [GateKeeperMiddleware],
                    ability: ({ gateKeeper, to }) => {
                        return (
                            gateKeeper.userHasAccessToCourse(to.params.courseId) &&
                            gateKeeper.userHasPermission('view instructor discussions report')
                        );
                    },
                },
                props: true,
                children: [
                    {
                        name: 'discussion-thread-report',
                        path: 'discussion-thread-report',
                        component: () => import('@/pages/courses/course-reports/DiscussionThreadReport.vue'),
                        meta: {
                            title: 'Course Discussion',
                        },
                        props: true,
                    },
                ],
            },

            {
                path: 'schedule/',
                component: () => import('@/pages/schedule/ScheduleIndex.vue'),
                props: true,
                children: [
                    {
                        name: 'course-activities-view-upcoming',
                        path: 'activities/upcoming',
                        component: () => import('@/components/activities/ActivitiesView.vue'),
                        meta: {
                            title: 'Due Dates',
                            middleware: [GateKeeperMiddleware],
                            ability: ({ gateKeeper }) => gateKeeper.userHasPermission('view an activity'),
                        },
                        props: true,
                    },

                    {
                        name: 'course-activities-view-previous',
                        path: 'activities/previous',
                        component: () => import('@/components/activities/ActivitiesView.vue'),
                        meta: {
                            title: 'Due Dates',
                            middleware: [GateKeeperMiddleware],
                            ability: ({ gateKeeper }) => gateKeeper.userHasPermission('view an activity'),
                        },
                        props: true,
                    },

                    {
                        name: 'course-time-periods-view-upcoming',
                        path: 'time-periods/upcoming',
                        component: () => import('@/components/time-periods/TimePeriodsView.vue'),
                        props: true,
                        meta: {
                            title: 'Schedules',
                            middleware: [GateKeeperMiddleware],
                            ability: { action: 'view all', subject: 'time periods' },
                        },
                    },

                    {
                        name: 'course-time-periods-view-previous',
                        path: 'time-periods/previous',
                        component: () => import('@/components/time-periods/TimePeriodsView.vue'),
                        props: true,
                        meta: {
                            title: 'Schedules',
                            middleware: [GateKeeperMiddleware],
                            ability: { action: 'view all', subject: 'time periods' },
                        },
                    },

                    {
                        name: 'course-meetings-view-upcoming',
                        path: 'meetings/upcoming',
                        component: () => import('@/components/meetings/MeetingsView.vue'),
                        props: true,
                        meta: {
                            title: 'Meetings',
                            middleware: [GateKeeperMiddleware],
                            ability: { action: 'view all', subject: 'meetings' },
                        },
                    },

                    {
                        name: 'course-meetings-view-previous',
                        path: 'meetings/previous',
                        component: () => import('@/components/meetings/MeetingsView.vue'),
                        props: true,
                        meta: {
                            title: 'Meetings',
                            middleware: [GateKeeperMiddleware],
                            ability: { action: 'view all', subject: 'meetings' },
                        },
                    },
                ],
            },
        ],
    },
];
